/**
 * Route builder helpers — single source of truth for mobile URL patterns.
 * All mobile surfaces live under /m/; project-scoped variants under /m/p/:id/.
 * Never hand-build these paths inline in page code.
 */

/** /m/tasks/:taskId */
export function taskDetailRoute(taskId: string | number): string {
  return `/m/tasks/${taskId}`
}

/** /m/p/:projectId/schedule or /m/schedule when no project */
export function scheduleRouteForScope(projectId?: string | number): string {
  return projectId != null ? `/m/p/${projectId}/schedule` : '/m/schedule'
}

/**
 * Surface-to-route mapping for all-projects ↔ project-scoped navigation.
 * Given the current path and a new projectId (or undefined for all-projects),
 * returns the equivalent URL at the target scope, or null if there is no
 * direct equivalent (caller should default to the scope's landing page).
 */
export function surfaceRouteForScope(
  currentPath: string,
  projectId?: string | number,
): string | null {
  const toProject = projectId != null

  if (toProject) {
    if (currentPath === '/m/schedule') return `/m/p/${projectId}/schedule`
    if (currentPath === '/m/rfis')    return `/m/p/${projectId}/rfi`
    if (currentPath === '/m/reports') return `/m/p/${projectId}/log`
    // Project hub is the generic landing for project scope
    return `/m/p/${projectId}`
  } else {
    // Switching to all-projects
    if (currentPath.match(/^\/m\/p\/[^/]+\/schedule$/)) return '/m/schedule'
    if (currentPath.match(/^\/m\/p\/[^/]+\/rfi$/))      return '/m/rfis'
    if (currentPath.match(/^\/m\/p\/[^/]+\/log$/))      return '/m/reports'
    // For project-only surfaces, fall back to today
    return '/m/today'
  }
}

/**
 * /m/p/:projectId/log or /m/reports when no project.
 * Pass `openToday: true` to have the log page skip the list and jump
 * straight into today's draft (resuming it, or creating one) — used by the
 * Today page's Daily Log quick action. Ignored when there's no project
 * scope, since /m/reports has no single-project draft to jump into.
 */
export function dailyLogRouteForScope(projectId?: string | number, opts?: { openToday?: boolean }): string {
  if (projectId == null) return '/m/reports'
  return opts?.openToday ? `/m/p/${projectId}/log?open=today` : `/m/p/${projectId}/log`
}
